import { useState, useEffect } from "react";
import { useEditorStore } from "@/store/editorStore";
import {
  Link as LinkIcon,
  Unlink,
  ChevronsUp,
  ChevronsDown,
  ChevronUp,
  ChevronDown,
  Trash2,
} from "lucide-react";

export default function ElementContextToolbar({ pageId, elementId }) {
  const updateElement = useEditorStore((s) => s.updateElement);
  const deleteElement = useEditorStore((s) => s.deleteElement);
  const changeLayer = useEditorStore((s) => s.changeLayer);
  const element = useEditorStore((s) =>
    s.pages.find((p) => p.id === pageId)?.elements.find((e) => e.id === elementId),
  );

  const [showLink, setShowLink] = useState(false);
  const [url, setUrl] = useState(element?.href || "");

  useEffect(() => {
    setUrl(element?.href || "");
    setShowLink(false);
  }, [elementId, element?.href]);

  if (!element) return null;

  const top = Math.max(0, element.y - 52);
  const left = element.x;

  const Btn = ({ icon: Icon, onClick, label, active, danger, testId }) => (
    <button
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${
        danger
          ? "text-red-600 hover:bg-red-50"
          : active
          ? "bg-blue-50 text-[#002FA7]"
          : "text-gray-700 hover:bg-gray-100"
      }`}
      data-testid={testId}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div
      className="absolute z-20"
      style={{ left, top }}
      onClick={(e) => e.stopPropagation()}
      data-testid="element-context-toolbar"
    >
      <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-md shadow-md p-1">
        <Btn icon={ChevronsUp} onClick={() => changeLayer(pageId, elementId, "front")} label="To front" testId="layer-to-front" />
        <Btn icon={ChevronUp} onClick={() => changeLayer(pageId, elementId, "forward")} label="Forward" testId="layer-forward" />
        <Btn icon={ChevronDown} onClick={() => changeLayer(pageId, elementId, "backward")} label="Backward" testId="layer-backward" />
        <Btn icon={ChevronsDown} onClick={() => changeLayer(pageId, elementId, "back")} label="To back" testId="layer-to-back" />
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <Btn
          icon={element.href ? LinkIcon : LinkIcon}
          onClick={() => setShowLink((s) => !s)}
          active={!!element.href || showLink}
          label="Hyperlink"
          testId="element-link-button"
        />
        {element.href && (
          <Btn
            icon={Unlink}
            onClick={() => {
              updateElement(pageId, elementId, { href: undefined });
              setUrl("");
            }}
            label="Remove link"
            testId="element-unlink-button"
          />
        )}
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <Btn icon={Trash2} onClick={() => deleteElement(pageId, elementId)} label="Delete" danger testId="element-delete-button" />
      </div>

      {showLink && (
        <div className="mt-1 flex items-center gap-1 bg-white border border-gray-200 rounded-md shadow-md p-1">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            className="px-2 py-1 text-sm w-56 outline-none"
            data-testid="element-link-input"
            autoFocus
          />
          <button
            className="text-xs bg-[#002FA7] text-white px-3 py-1 rounded"
            onClick={() => {
              updateElement(pageId, elementId, { href: url || undefined });
              setShowLink(false);
            }}
            data-testid="element-link-save"
          >
            Save link
          </button>
        </div>
      )}
    </div>
  );
}
